import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
// Local-timezone-aware YYYY-MM-DD (avoids UTC off-by-one when IST crosses midnight).
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function ReleasePaymentDialog({ open, onOpenChange, request, onReleased }) {
  const [reference, setReference] = useState("");
  const [method, setMethod] = useState("UPI");
  const [note, setNote] = useState("");
  const [proofFileId, setProofFileId] = useState("");
  const [releasedOn, setReleasedOn] = useState(todayISO());
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReference(""); setMethod("UPI"); setNote("");
      setProofFileId(""); setReleasedOn(todayISO());
    }
  }, [open]);

  const uploadProof = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofFileId(data.file_id);
      toast.success("Screenshot staged");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const canSubmit = useMemo(
    () => !!(reference.trim() || proofFileId),
    [reference, proofFileId],
  );

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please add a UTR number or upload a screenshot");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/approvals/${request.id}/release`, {
        reference_number: reference.trim(),
        method, note,
        release_proof_file_id: proofFileId || "",
        released_on: releasedOn || todayISO(),
      });
      toast.success("Payment released & logged");
      onReleased?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Release failed");
    } finally { setSubmitting(false); }
  };

  if (!request) return null;

  const isCost = request.entity_kind === "cost" || !!request.cost_id;
  const title = isCost
    ? `${request.cost_type_snapshot || "Additional cost"} · ${request.payment_type} · ${fmtCur(request.amount)}`
    : `@${request.creator_username_snapshot} · ${request.payment_type} · ${fmtCur(request.amount)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="release-payment-dialog">
        <DialogHeader>
          <DialogTitle>Release payment</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2 p-3 bg-slate-50 border border-slate-200 rounded-md text-xs">
            <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Gross</div><div className="font-mono font-semibold text-slate-900">{fmtCur(request.amount)}</div></div>
            <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">TDS ({request.tds_rate}%)</div><div className="font-mono font-semibold text-rose-700">− {fmtCur(request.tds_amount)}</div></div>
            <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Net</div><div className="font-mono font-semibold text-emerald-700">{fmtCur(request.net_amount)}</div></div>
          </div>
          <div className="text-[11px] text-slate-500">TDS is auto-applied and not editable.</div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Payment Released On</Label>
              <Input
                type="date"
                value={releasedOn}
                onChange={(e) => setReleasedOn(e.target.value)}
                max={todayISO()}
                className="mt-1"
                data-testid="release-date"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Method</Label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm h-10" data-testid="release-method">
                {["UPI", "IMPS", "NEFT", "Bank Transfer", "Cash", "Other"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Reference / UTR number</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR123456…" className="mt-1 font-mono" data-testid="release-reference" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Payment screenshot</Label>
            <Input type="file" accept="image/*,.pdf" onChange={(e) => e.target.files?.[0] && uploadProof(e.target.files[0])} className="mt-1" data-testid="release-proof" />
            {uploading && <div className="text-[11px] text-slate-500 mt-1">Uploading…</div>}
            {proofFileId && <div className="text-[11px] text-emerald-700 mt-1">Screenshot staged ✓</div>}
          </div>
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            Add a UTR number <b>or</b> a screenshot — at least one is required.
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Note (optional)</Label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="release-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="release-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting || !canSubmit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="release-confirm">
            {submitting ? "Releasing…" : `Release ${fmtCur(request.net_amount)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
